import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { PageTitle, Panel, Field, TextInput, TextArea, Btn } from "../components/ui";
import { useI18n } from "../i18n";
import { CL } from "../api";

const empty = { company_name: "", brand_color: "#14512f", logo_data_url: "", footer_note: "" };

export default function Branding() {
  const { t } = useI18n();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    CL.getBranding().then((b) => setForm({ ...empty, ...b })).catch(() => {});
  }, []);

  const up = (k) => (e) => { const v = e.target.value; setForm((p) => ({ ...p, [k]: v })); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 750 * 1024) { toast.error(t("logoTooLarge")); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, logo_data_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try { const saved = await CL.updateBranding(form); setForm({ ...empty, ...saved }); toast.success(t("brandingSaved")); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="branding-page">
      <PageTitle title={t("brandingTitle")} subtitle={t("brandingDesc")} />
      <Panel className="max-w-3xl">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field label={t("companyName")}>
            <TextInput data-testid="branding-company" value={form.company_name} onChange={up("company_name")} placeholder="Örn. Yeşil Danışmanlık" />
          </Field>
          <Field label={t("brandColor")}>
            <div className="flex items-center gap-3">
              <input type="color" data-testid="branding-color" value={form.brand_color || "#14512f"} onChange={up("brand_color")}
                className="h-10 w-12 cursor-pointer rounded-lg border border-[#dcdfd4] bg-white" />
              <TextInput value={form.brand_color} onChange={up("brand_color")} placeholder="#14512f" />
            </div>
          </Field>
        </div>

        <div className="mt-5">
          <div className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#8a8f83]">{t("logo")}</div>
          <div className="flex items-center gap-4">
            <div className="grid h-20 w-40 place-items-center overflow-hidden rounded-lg border border-dashed border-[#d7dacd] bg-[#fbfcf8]">
              {form.logo_data_url
                ? <img src={form.logo_data_url} alt="logo" className="max-h-16 max-w-[9rem] object-contain" data-testid="branding-logo-preview" />
                : <span className="text-xs text-[#a7ada0]">Logo</span>}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={onFile} className="hidden" data-testid="branding-logo-input" />
              <Btn variant="outline" onClick={() => fileRef.current?.click()} data-testid="branding-upload"><Upload className="h-4 w-4" />{t("uploadLogo")}</Btn>
              {form.logo_data_url && (
                <Btn variant="danger" onClick={() => setForm((p) => ({ ...p, logo_data_url: "" }))} data-testid="branding-remove"><Trash2 className="h-4 w-4" />{t("removeLogo")}</Btn>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <Field label={t("footerNote")}>
            <TextArea data-testid="branding-footer" value={form.footer_note} onChange={up("footer_note")} placeholder="Örn. Yeşil Danışmanlık tarafından hazırlanmıştır." />
          </Field>
        </div>

        <div className="mt-6 flex items-center gap-4">
          <Btn onClick={save} disabled={saving} data-testid="branding-save">{saving ? "..." : t("save")}</Btn>
          <span className="text-xs text-[#8a8f83]">{t("brandingHint")}</span>
        </div>
      </Panel>
    </div>
  );
}
